import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useSapiensAuth } from '@sapiens/auth-react';
import { MainLayout } from '@/components/layout';
import { LoadingSpinner } from '@/components/common';
import { usersApi, type UserInfo } from '@/api';
import { UserRoleDialog } from './UserRoleDialog';

const getRoleColor = (userType: number) => {
  switch (userType) {
    case 0:
      return 'warning'; // not activated
    case 1:
      return 'primary'; // normal user
    case 2:
      return 'secondary'; // admin
    case 3:
      return 'error'; // frozen
    default:
      return 'default';
  }
};

const getRoleKey = (userType: number) => {
  switch (userType) {
    case 0:
      return 'notActivated';
    case 1:
      return 'user';
    case 2:
      return 'admin';
    case 3:
      return 'frozen';
    default:
      return 'user';
  }
};

export function UserListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user: currentUser } = useSapiensAuth();

  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserInfo | null>(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [userToEditRole, setUserToEditRole] = useState<UserInfo | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await usersApi.getUsers();
      if (response.success && response.data) {
        setUsers(response.data);
      }
    } catch (err) {
      enqueueSnackbar(t('error.serverError'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = users.filter((user) => {
    const query = searchQuery.toLowerCase();
    return (
      user.email.toLowerCase().includes(query) ||
      (user.name?.toLowerCase().includes(query) ?? false)
    );
  });

  const handleDeleteClick = (user: UserInfo) => {
    if (user.id === currentUser?.id) {
      enqueueSnackbar(t('admin.cannotDeleteSelf'), { variant: 'warning' });
      return;
    }
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      await usersApi.deleteUser(userToDelete.id);
      enqueueSnackbar(t('admin.deleteSuccess'), { variant: 'success' });
      setUsers(users.filter((u) => u.id !== userToDelete.id));
    } catch (err) {
      enqueueSnackbar(t('error.serverError'), { variant: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleRoleClick = (user: UserInfo) => {
    if (user.id === currentUser?.id) {
      enqueueSnackbar(t('admin.cannotChangeOwnRole'), { variant: 'warning' });
      return;
    }
    setUserToEditRole(user);
    setRoleDialogOpen(true);
  };

  const handleRoleChange = async (userType: number) => {
    if (!userToEditRole) return;

    try {
      const response = await usersApi.updateUserRole(userToEditRole.id, { userType });
      if (response.success && response.data) {
        enqueueSnackbar(t('admin.roleUpdated'), { variant: 'success' });
        setUsers(users.map((u) => (u.id === userToEditRole.id ? response.data! : u)));
      }
    } catch (err) {
      enqueueSnackbar(t('error.serverError'), { variant: 'error' });
    } finally {
      setRoleDialogOpen(false);
      setUserToEditRole(null);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <LoadingSpinner />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('admin.userManagement')}
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder={t('common.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, maxWidth: 400 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/users/create')}
          >
            {t('admin.createUser')}
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('auth.email')}</TableCell>
              <TableCell>{t('user.name')}</TableCell>
              <TableCell>{t('user.role')}</TableCell>
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar src={user.avatar} sx={{ width: 32, height: 32 }}>
                      {user.name?.[0] || user.email[0]}
                    </Avatar>
                    {user.email}
                  </Box>
                </TableCell>
                <TableCell>{user.name || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={t(`roles.${getRoleKey(user.userType)}`)}
                    color={getRoleColor(user.userType)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleRoleClick(user)}
                    title={t('admin.changeRole')}
                    disabled={user.id === currentUser?.id}
                  >
                    <BadgeIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/admin/users/${user.id}/edit`)}
                    title={t('common.edit')}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteClick(user)}
                    title={t('common.delete')}
                    disabled={user.id === currentUser?.id}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="text.secondary" py={4}>
                    {searchQuery ? t('common.noResults') : t('admin.noUsers')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('admin.deleteUser')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('admin.confirmDelete')}
            <br />
            <strong>{userToDelete?.email}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role Change Dialog */}
      <UserRoleDialog
        open={roleDialogOpen}
        user={userToEditRole}
        onClose={() => {
          setRoleDialogOpen(false);
          setUserToEditRole(null);
        }}
        onSave={handleRoleChange}
      />
    </MainLayout>
  );
}
